import { useEffect, useState, useCallback } from "react";
import { api, imageUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { Leaf, MapPin, Butterfly, Tree, Flower, Bird, PawPrint, Plant, FunnelSimple } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ConfidenceBadge from "@/components/ConfidenceBadge";

const TYPE_META = {
  plant: { icon: Plant, color: "#16A34A", label: "Plants" },
  tree: { icon: Tree, color: "#15803D", label: "Trees" },
  flower: { icon: Flower, color: "#DB2777", label: "Flowers" },
  insect: { icon: Butterfly, color: "#D97706", label: "Insects" },
  bird: { icon: Bird, color: "#0EA5E9", label: "Birds" },
  animal: { icon: PawPrint, color: "#7C3AED", label: "Animals" },
  fungus: { icon: Leaf, color: "#B45309", label: "Fungi" },
  other: { icon: Leaf, color: "#64748B", label: "Other" },
};

export default function NatureGuide() {
  const [guide, setGuide] = useState(null);
  const [country, setCountry] = useState("all");
  const [city, setCity] = useState("all");

  const load = useCallback(() => {
    const params = {};
    if (country !== "all") params.country = country;
    if (city !== "all") params.city = city;
    api.get("/nature/guide", { params }).then((r) => setGuide(r.data)).catch(() => setGuide({ total_species: 0, types: {}, available: {} }));
  }, [country, city]);
  useEffect(() => { load(); }, [load]);

  const avail = guide?.available || { countries: [], cities: [] };

  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-[#16A34A]">NaturePulse</p>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Species Field Guide</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            AI-identified plants, trees, flowers, insects, birds and more — grouped by type, with where each was seen.
            {guide && <span className="ml-1 font-semibold text-zinc-700">{guide.total_species} species catalogued.</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FunnelSimple size={18} className="text-muted-foreground" />
          <Select value={country} onValueChange={(v) => { setCountry(v); setCity("all"); }}>
            <SelectTrigger data-testid="guide-country" className="h-9 w-44 rounded-md"><SelectValue placeholder="Region" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              {avail.countries?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger data-testid="guide-city" className="h-9 w-44 rounded-md"><SelectValue placeholder="City" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {avail.cities?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {guide === null ? (
        <p className="mt-8 text-muted-foreground">Loading…</p>
      ) : Object.keys(guide.types || {}).length === 0 ? (
        <div className="mt-16 grid place-items-center text-center">
          <Leaf size={44} weight="thin" className="text-zinc-300" />
          <p className="mt-3 font-display font-bold">No species identified yet</p>
          <p className="text-sm text-muted-foreground">Upload nature photos — the AI will identify species automatically.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-10">
          {Object.entries(guide.types).map(([type, items]) => {
            const meta = TYPE_META[type] || TYPE_META.other;
            return (
              <section key={type}>
                <div className="flex items-center gap-2">
                  <meta.icon size={22} weight="fill" style={{ color: meta.color }} />
                  <h2 className="font-display text-xl font-bold tracking-tight">{meta.label}</h2>
                  <span className="font-mono-p text-sm text-muted-foreground">{items.length} species</span>
                </div>
                <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((sp, i) => (
                    <Card key={i} data-testid={`species-${type}-${i}`} className="overflow-hidden rounded-md border border-border shadow-none">
                      <div className="grid grid-cols-3 gap-0.5 bg-zinc-100">
                        {sp.photo_ids.slice(0, 3).map((pid) => (
                          <Link key={pid} to={`/photos/${pid}`}>
                            <img src={imageUrl(pid)} alt={sp.common_name} className="aspect-square w-full object-cover transition-transform duration-300 hover:scale-105" />
                          </Link>
                        ))}
                      </div>
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-display font-bold leading-tight">{sp.common_name || "Unknown"}</h3>
                            {sp.scientific_name && <p className="font-mono-p text-xs italic" style={{ color: meta.color }}>{sp.scientific_name}</p>}
                          </div>
                          <ConfidenceBadge score={sp.max_confidence} />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span className="rounded-sm bg-zinc-100 px-2 py-0.5 font-semibold text-zinc-700">{sp.count} photo{sp.count > 1 ? "s" : ""}</span>
                          {sp.places?.slice(0, 2).map((pl) => (
                            <span key={pl} className="flex items-center gap-0.5"><MapPin size={11} weight="fill" />{pl}</span>
                          ))}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
